// src/features/gatherings/hooks/data/usePendingEditSweep.ts
//
// ENG-1097: finds gatherings with a staged, awaiting-review host edit.
//
// Why a sweep and not a list endpoint: a staged content edit deliberately
// leaves the gathering PUBLISHED (that is the whole point of ENG-856 — the live
// version stays visible and registerable while the change awaits approval), so
// such a gathering never moves into a review column. The backend also only
// populates `has_pending_edit` on the authenticated single-gathering read;
// `GET /gatherings/search` always leaves it unset. There is no
// "list pending edits" endpoint, so the only way to flag them on the board is
// one `GET /gatherings/{id}/pending-edit` per gathering.
//
// That fanout is bounded three ways, because unbounded fanout against this API
// has exhausted the backend connection pool before (ENG-933, ENG-972):
//   * every request goes through a shared 3-wide limiter in the endpoint layer;
//   * answers (including "no pending edit") are cached for 30s, so re-renders
//     and column re-sorts are free;
//   * by default only UPCOMING gatherings are swept. A staged edit on a past
//     gathering cannot change anything a guest is still acting on, and once
//     ENG-1096 lands those edits hard-expire a day before the start anyway.
//     `includePastGatherings` opts into the full set for a manual re-check.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gathering, GatheringPendingEdit } from '../../../../types';
import {
  fetchGatheringPendingEdit,
  invalidateAllPendingEditCaches
} from '../../../../services/api/endpoints/gatherings';
import { isAbortError } from '../../../../services/api/utils/concurrencyLimit';

export interface UsePendingEditSweepOptions {
  /** When false the hook makes no requests (e.g. a collapsed column). */
  enabled?: boolean;
  /** Sweep past gatherings too — used by the manual re-check. */
  includePastGatherings?: boolean;
}

export interface UsePendingEditSweepResult {
  /** Staged edits found, keyed by gathering id. Absent means "none staged". */
  pendingEdits: Record<string, GatheringPendingEdit>;
  /** How many of the swept gatherings have a staged edit. */
  pendingEditCount: number;
  isSweeping: boolean;
  /** Set when at least one lookup failed, so the count may be understated. */
  error: string | null;
  /** Re-runs the sweep, bypassing the cached answers. */
  refresh: () => void;
}

/** Today at local midnight — the cutoff for "upcoming". */
const startOfToday = (): Date => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

const isUpcoming = (gathering: Gathering): boolean => {
  if (!gathering.date) {
    // No date to judge by: include it rather than silently skipping a
    // gathering that might have an unapproved edit.
    return true;
  }
  // The API returns YYYY-MM-DD; parse the parts so the comparison is not
  // shifted by the browser's UTC interpretation of a bare date string.
  const [year, month, day] = gathering.date.split('T')[0].split('-');
  const gatheringDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day)
  );
  if (Number.isNaN(gatheringDate.getTime())) {
    return true;
  }
  return gatheringDate >= startOfToday();
};

export const usePendingEditSweep = (
  gatherings: Gathering[],
  token: string | null,
  { enabled = true, includePastGatherings = false }: UsePendingEditSweepOptions = {}
): UsePendingEditSweepResult => {
  const [pendingEdits, setPendingEdits] = useState<Record<string, GatheringPendingEdit>>({});
  const [isSweeping, setIsSweeping] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sweepNonce, setSweepNonce] = useState<number>(0);

  // The board re-creates its gathering array on every render (filter + sort),
  // so the effect keys off a stable string of ids rather than the array
  // identity. Ids are UUIDs, so a comma join is unambiguous.
  const candidateKey = useMemo(() => {
    const ids = gatherings
      .filter((gathering) => includePastGatherings || isUpcoming(gathering))
      .map((gathering) => gathering.id);
    // Sort so a re-sort of the column does not look like a new candidate set.
    return ids.slice().sort().join(',');
  }, [gatherings, includePastGatherings]);

  // Guards a setState after unmount without swallowing real errors.
  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !token || candidateKey === '') {
      return undefined;
    }

    const ids = candidateKey.split(',');
    const controller = new AbortController();
    let cancelled = false;

    setIsSweeping(true);
    setError(null);

    Promise.all(
      ids.map((id) =>
        fetchGatheringPendingEdit(token, id, controller.signal)
          .then((pendingEdit) => ({ id, pendingEdit, error: null as string | null }))
          .catch((err: unknown) => {
            if (isAbortError(err)) {
              throw err;
            }
            // One gathering failing must not blank the whole sweep — report it
            // and keep the rest of the flags.
            console.error(`Error checking pending edit for gathering ${id}:`, err);
            return {
              id,
              pendingEdit: null,
              error: err instanceof Error ? err.message : String(err)
            };
          })
      )
    )
      .then((results) => {
        if (cancelled || !isMountedRef.current) {
          return;
        }

        setPendingEdits((previous) => {
          const next: Record<string, GatheringPendingEdit> = {};
          // Keep flags for gatherings outside this sweep (e.g. filtered out of
          // the column right now) so badges do not flicker off.
          Object.keys(previous).forEach((id) => {
            if (ids.indexOf(id) === -1) {
              next[id] = previous[id];
            }
          });
          results.forEach(({ id, pendingEdit }) => {
            if (pendingEdit) {
              next[id] = pendingEdit;
            }
          });
          return next;
        });

        const failures = results.filter((result) => result.error !== null);
        setError(
          failures.length === 0
            ? null
            : `Could not check ${failures.length} of ${results.length} gatherings for pending edits.`
        );
      })
      .catch((err: unknown) => {
        if (isAbortError(err)) {
          // Expected when the candidate set changes mid-sweep.
          return;
        }
        if (!cancelled && isMountedRef.current) {
          setError('Could not check for pending edits.');
        }
      })
      .finally(() => {
        if (!cancelled && isMountedRef.current) {
          setIsSweeping(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, candidateKey, enabled, sweepNonce]);

  const refresh = useCallback(() => {
    // Clear the cached answers first, otherwise a re-check inside the cache
    // window would replay the same result it is meant to re-verify.
    invalidateAllPendingEditCaches();
    setSweepNonce((nonce) => nonce + 1);
  }, []);

  const pendingEditCount = Object.keys(pendingEdits).length;

  return { pendingEdits, pendingEditCount, isSweeping, error, refresh };
};

export default usePendingEditSweep;
